import ReactECharts from 'echarts-for-react';

/**
 * AuditTrailChart_2 — "minimal editorial" variant of AuditTrailChart (blog04).
 * Same real data: documented AI incidents per year (AI Incident Database, as
 * reported by Stanford HAI AI Index 2024–2026 — see AuditTrailChart.tsx header
 * for the per-year citations; mirrors public/blog/ai-incidents-by-year.csv).
 * Opaque white card, generous whitespace, thin strokes, muted slate with a
 * single teal accent. Quiet magazine-infographic feel — no gradients, glows,
 * or shadows.
 */

const PALETTE = {
  accent: '#0AB3BF', // single teal accent
  text: '#64748B',
  textSoft: '#94A3B8',
  hairline: '#E2E8F0',
  surface: '#FFFFFF',
};

const YEARS = ['2023', '2024', '2025'];
// Documented AI incidents per year — Stanford HAI AI Index 2024/2025/2026.
const INCIDENTS = [123, 233, 362];

/** ONE shared count formatter — tooltip, axis ticks, and bar labels all use it. */
const formatCount = (v: number) => `${v}`;

export const AuditTrailChart_2 = ({ height = 340 }: { height?: number }) => {
  const option = {
    backgroundColor: PALETTE.surface,
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'line', lineStyle: { color: PALETTE.hairline, width: 1 } },
      backgroundColor: PALETTE.surface,
      borderColor: PALETTE.hairline,
      borderWidth: 1,
      textStyle: { color: PALETTE.text, fontSize: 12 },
      formatter: (params: { value: number; dataIndex: number }[]) =>
        `${YEARS[params[0].dataIndex]}<br/>documented AI incidents: <b>${formatCount(params[0].value)}</b>`,
    },
    grid: { left: 48, right: 32, top: 52, bottom: 40 },
    xAxis: {
      type: 'category',
      data: YEARS,
      axisLine: { lineStyle: { color: PALETTE.hairline, width: 1 } },
      axisLabel: { color: PALETTE.text, fontSize: 11 },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'value',
      name: 'documented incidents',
      nameTextStyle: { color: PALETTE.textSoft, fontSize: 11 },
      axisLabel: { color: PALETTE.textSoft, fontSize: 11, formatter: (v: number) => formatCount(v) },
      splitLine: { lineStyle: { color: PALETTE.hairline, width: 1 } },
    },
    series: [
      {
        name: 'Documented AI incidents',
        type: 'bar',
        barWidth: '32%',
        itemStyle: { color: PALETTE.accent },
        data: INCIDENTS,
        label: {
          show: true,
          position: 'top',
          color: PALETTE.text,
          fontSize: 11,
          formatter: (p: { value: number }) => formatCount(p.value),
        },
        markLine: {
          silent: true,
          symbol: 'none',
          lineStyle: { color: PALETTE.textSoft, type: 'dashed', width: 1 },
          label: {
            position: 'insideEndTop',
            color: PALETTE.text,
            fontSize: 11,
            formatter: 'under 100 every year until 2022',
          },
          data: [{ yAxis: 100 }],
        },
      },
    ],
  };

  return (
    <div className="my-8 rounded-xl overflow-hidden border border-slate-200 bg-white p-4">
      <ReactECharts option={option} style={{ height }} opts={{ renderer: 'canvas' }} />
      <p className="mt-2 text-xs text-slate-500 text-center">
        Figure 1. Documented AI incidents nearly tripled in two years (123 → 362). Every incident on this curve is an
        event where someone needed a receipt. Source: Stanford HAI AI Index 2024–2026.
      </p>
      <p className="mt-1 text-label-xs text-slate-400 text-center">
        AI Incident Database backfills historical years; per-year totals can be revised upward (AI Index 2026 note).
      </p>
    </div>
  );
};
